import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Button, CircularProgress,
  Chip, Alert, Divider, Paper, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  getTailoredResume,
  tailorResume,
  downloadTailoredResume,
  getApplications,
} from '../services/api';
import { useNotification } from '../context/NotificationContext';

/**
 * Render tailored Markdown as React elements.
 *
 * Grammar supported (matches the resume_tailor skill output):
 *   # text       → h4 (candidate name — MUI Typography h4)
 *   ## text      → h6 (title)
 *   ### text     → subtitle1 bold UPPERCASE (section)
 *   - text       → bullet list item
 *   **text**     → bold inline
 *   other        → body paragraph
 *
 * Blank lines separate blocks (we collapse consecutive blanks).
 */
function renderInlineBold(text, keyBase) {
  // Split on **...** segments and render bold where matched
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return (
        <Box
          key={`${keyBase}-${idx}`}
          component="strong"
          sx={{ fontWeight: 700 }}
        >
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <span key={`${keyBase}-${idx}`}>{part}</span>;
  });
}

function MarkdownPreview({ markdown }) {
  if (!markdown) return null;
  const lines = markdown.split('\n');
  const blocks = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (bulletBuffer.length) {
      blocks.push(
        <Box key={`ul-${blocks.length}`} component="ul" sx={{ pl: 3, my: 1 }}>
          {bulletBuffer.map((b, i) => (
            <Box key={i} component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2">
                {renderInlineBold(b, `li-${blocks.length}-${i}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
      bulletBuffer = [];
    }
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();
    if (!line) {
      flushBullets();
      return;
    }

    if (line.startsWith('### ')) {
      flushBullets();
      blocks.push(
        <Typography
          key={`h3-${idx}`}
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            mt: 2.5,
            pb: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {renderInlineBold(line.slice(4), `h3-${idx}`)}
        </Typography>
      );
    } else if (line.startsWith('## ')) {
      flushBullets();
      blocks.push(
        <Typography
          key={`h2-${idx}`}
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 400, mb: 1 }}
        >
          {renderInlineBold(line.slice(3), `h2-${idx}`)}
        </Typography>
      );
    } else if (line.startsWith('# ')) {
      flushBullets();
      blocks.push(
        <Typography
          key={`h1-${idx}`}
          variant="h4"
          sx={{ fontWeight: 700, mb: 0 }}
        >
          {renderInlineBold(line.slice(2), `h1-${idx}`)}
        </Typography>
      );
    } else if (line.startsWith('- ')) {
      bulletBuffer.push(line.slice(2));
    } else {
      flushBullets();
      blocks.push(
        <Typography key={`p-${idx}`} variant="body2" sx={{ mb: 1 }}>
          {renderInlineBold(line, `p-${idx}`)}
        </Typography>
      );
    }
  });
  flushBullets();

  return <Box>{blocks}</Box>;
}

export default function Tailor() {
  const { t } = useTranslation();
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { success, error: notifyError, info } = useNotification();

  const [app, setApp] = useState(null);
  const [tailor, setTailor] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | generating | ready | error
  const [error, setError] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const generationTriggered = useRef(false);

  // Load application details + existing tailored resume on mount
  useEffect(() => {
    if (!applicationId) return;
    let cancelled = false;

    const load = async () => {
      setStatus('loading');
      setError('');
      try {
        // Fetch applications list to find this one (no single-GET endpoint exists yet)
        const appsRes = await getApplications();
        const appData = appsRes.data.find((a) => String(a.id) === String(applicationId));
        if (cancelled) return;
        if (!appData) {
          setError(t('tailor.app_not_found'));
          setStatus('error');
          return;
        }
        setApp(appData);

        // Check for existing tailored resume
        try {
          const tRes = await getTailoredResume(applicationId);
          if (cancelled) return;
          setTailor(tRes.data);
          setStatus('ready');
        } catch (err) {
          if (cancelled) return;
          if (err.response?.status === 404) {
            // No tailored resume yet — auto-generate
            if (generationTriggered.current) return;
            generationTriggered.current = true;
            await runGeneration({ regenerate: false });
          } else {
            setError(err.friendlyMessage || t('tailor.load_failed'));
            setStatus('error');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.friendlyMessage || t('tailor.app_load_failed'));
          setStatus('error');
        }
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const runGeneration = async ({ regenerate }) => {
    setStatus('generating');
    setError('');
    try {
      const res = await tailorResume(applicationId, regenerate);
      setTailor(res.data);
      setStatus('ready');
      if (regenerate) {
        success(t('tailor.regen_success'));
      } else if (!res.data.cached) {
        success(t('tailor.tailor_success'));
      }
    } catch (err) {
      setError(err.friendlyMessage || t('tailor.gen_failed'));
      setStatus('error');
      notifyError(err.friendlyMessage || t('tailor.gen_short'));
    }
  };

  const handleRegenerate = async () => {
    if (regenerating) return;
    const regensLeft = tailor?.regens_remaining_for_app ?? 0;
    if (regensLeft <= 0) {
      notifyError(t('tailor.no_regens_toast'));
      return;
    }
    setRegenerating(true);
    await runGeneration({ regenerate: true });
    setRegenerating(false);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await downloadTailoredResume(applicationId);
      // Extract filename from Content-Disposition if present
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `tailored_${applicationId}.docx`;

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      info(t('tailor.download_success', { filename }));
    } catch (err) {
      notifyError(err.friendlyMessage || t('tailor.download_failed'));
    }
    setDownloading(false);
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  if (status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'generating') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
        <CircularProgress size={56} />
        <Typography variant="h6" color="text.secondary">
          {t('tailor.generating_title', { role: app?.role || '' })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('tailor.generating_hint')}
        </Typography>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box p={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')}>
          {t('tailor.back_to_apps')}
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Box>
    );
  }

  const regensLeft = tailor?.regens_remaining_for_app ?? 0;
  const dailyLeft = tailor?.tailors_remaining_today ?? 0;

  return (
    <Box p={3} maxWidth={1000} mx="auto">
      {/* Breadcrumb / Back */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        {t('tailor.back_to_apps')}
      </Button>

      {/* Header */}
      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <AutoFixHighIcon color="primary" />
                <Typography variant="h5" fontWeight="bold">
                  {t('tailor.page_title')}
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">
                <Trans
                  i18nKey="tailor.for_role"
                  values={{ role: app?.role, company: app?.company }}
                  components={{ 1: <strong />, 3: <strong /> }}
                />
              </Typography>
              {app?.location && (
                <Typography variant="caption" color="text.disabled">
                  {app.location}
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Chip
                size="small"
                label={t('tailor.version_label', { n: tailor?.generation_count ?? 1 })}
                color="primary"
                variant="outlined"
              />
              <Chip
                size="small"
                label={t('tailor.regens_left_chip', { n: regensLeft })}
                variant="outlined"
              />
              <Chip
                size="small"
                label={t('tailor.daily_left_chip', { n: dailyLeft })}
                variant="outlined"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Actions */}
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownload}
              disabled={downloading}
            >
              {t('tailor.download')}
            </Button>
            <Tooltip title={regensLeft <= 0 ? t('tailor.no_regens_tooltip') : t('tailor.regen_tooltip')}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                  onClick={handleRegenerate}
                  disabled={regenerating || regensLeft <= 0}
                >
                  {t('tailor.regenerate_with_count', { n: regensLeft })}
                </Button>
              </span>
            </Tooltip>
            {app?.url && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                component="a"
                href={app.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('tailor.open_job')}
              </Button>
            )}
          </Box>

          {/* Discreet AI translation disclaimer — appears only when the JD
              language differs from the user's UI language. backend now
              returns target_language on the tailor response. */}
          {tailor?.target_language && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}
            >
              {t('tailor.translated_disclaimer', { to: tailor.target_language })}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            <Trans
              i18nKey="tailor.bold_disclaimer"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </CardContent>
      </Card>

      {/* Markdown preview */}
      <Paper sx={{ p: 4, borderRadius: 3, bgcolor: 'white', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <MarkdownPreview markdown={tailor?.tailored_markdown || ''} />
      </Paper>
    </Box>
  );
}
